import React, { FunctionComponent, useContext, useState } from 'react';
import { Button, palettes, Modal, Body, useFlashMessage, ReadOnlyField } from '@indigo-ag/component-module';
import { AgStateName, GuaranteeData } from '@indigo-ag/schema';
import { faCheck, faPencil, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { InputNumberMask, parseNumberToLocale, PhaseSection } from 'shared';

interface PricingTableProps {}

export const PricingTable: FunctionComponent<PricingTableProps> = () => {
  /**
   * Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  /**
   * state
   */
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [pricing, setPricing] = useState({
    pricePerBag: agFinance?.guaranteeData?.pricePerBag,
    bagsPerHectares: agFinance?.guaranteeData?.bagsPerHectares
  });
  const { addFlashMessage } = useFlashMessage();

  /**
   * Mutations
   */
  const [upsertAgFinancingFn, { loading }] = useMutation(upsertFinancingAgFinancing);

  /**
   * Callbacks
   */
  const handlePricingChange = (field: keyof GuaranteeData) => {
    return (value: number | null): void => {
      setPricing({
        ...pricing,
        [field]: value || 0
      });
    };
  };

  const savePricing = () => {
    setShowConfirmationMessage(false);
    upsertAgFinancingFn({
      variables: {
        data: {
          id: agFinance?.id,
          guaranteeData: { id: agFinance?.guaranteeData?.id, ...pricing }
        }
      }
    })
      .then(() => {
        addFlashMessage({
          content: i18next.t('informationUpdated', { subject: `${i18next.t('pricingTable')}` }),
          title: i18next.t('success'),
          variant: 'success'
        });
        setIsEditing(false);
      })
      .catch(() =>
        addFlashMessage({
          content: i18next.t('error'),
          title: `No ${i18next.t('success')}`,
          variant: 'error'
        })
      );
  };

  const canEditPricingTable = agFinance?.currentStates?.includes(AgStateName.PHASE_2);

  return (
    <PhaseSection title={i18next.t('pricingTable')} subtitle={i18next.t('pricingDescription', { process: 'Luchesi' })}>
      <div className="flex flex-space-btw mt-2">
        {isEditing ? (
          <>
            <InputNumberMask
              label={i18next.t('bagsPriceBase')}
              name="pricePerBag"
              onNumberChange={handlePricingChange('pricePerBag')}
              numberValue={Number(pricing?.pricePerBag) || 0}
            />
            <InputNumberMask
              label={i18next.t('bagsBase')}
              name="bagsPerHectares"
              onNumberChange={handlePricingChange('bagsPerHectares')}
              numberValue={Number(pricing?.bagsPerHectares) || 0}
            />
            <div className="flex flex-space-btw mt-3" style={{ width: '210px' }}>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                {i18next.t('cancel')}
              </Button>
              <Button
                onClick={() => setShowConfirmationMessage(true)}
                busy={loading}
                disabled={!(pricing?.pricePerBag || pricing?.bagsPerHectares)}
              >
                {i18next.t('save')}
              </Button>
            </div>
            <Modal
              actionButtons={[
                {
                  onClick: () => savePricing(),
                  label: i18next.t('save'),
                  variant: 'primary',
                  faIcon: faCheck
                },
                {
                  onClick: () => setShowConfirmationMessage(false),
                  label: i18next.t('cancel'),
                  variant: 'secondary',
                  faIcon: faTimes
                }
              ]}
              open={showConfirmationMessage}
              renderContent={() => <Body>{i18next.t('pricingSaveConfirmation')}</Body>}
              title={i18next.t('areYouSure')}
            />
          </>
        ) : (
          <>
            <ReadOnlyField label={i18next.t('bagsPriceBase')} value={parseNumberToLocale(pricing?.pricePerBag) || ''} />
            <ReadOnlyField label={i18next.t('bagsBase')} value={parseNumberToLocale(pricing?.bagsPerHectares) || ''} />
            <Button
              onClick={() => setIsEditing(true)}
              faIcon={faPencil}
              variant="tertiary"
              size="small"
              faIconColor={palettes.brand.gray[600]}
              disabled={!canEditPricingTable}
            >
              {i18next.t('edit')}
            </Button>
          </>
        )}
      </div>
    </PhaseSection>
  );
};
